<template>
  <section id="mailbox-providers" class="mb-10">
    <page-header
      :breadcrumbs="breadcrumbs"
      header-col="5"
      :title="$dictionary.app.mailboxProvider.index.title"
    >
      <v-col
        cols="12"
        md="8"
        lg="7"
        class="d-flex align-center justify-end flex-wrap flex-lg-nowrap"
      >
        <debounce-search-text-field
          prop-to-search="name"
          id="search-mailbox-provider-btn"
          :lazy-search="getMailboxProvidersList"
          placeholder="Search Mailbox providers..."
          :no-of-records="mailboxParams.per_page || 10"
          :sort-by="sortConfig.sortBy"
          :sort-order="sortConfig.sortOrder"
          @search-value="searchMailboxName = $event"
        />

        <v-btn
          color="secondary"
          outlined
          link
          :to="$paths.createMailboxProvider"
          class="button add-seed-btn font-weight-bold btn-light-grey-border text--secondary"
        >
          <v-icon class="pr-3 text--secondary">
            mdi-plus
          </v-icon>
          Add Mailbox Provider
        </v-btn>
        <csv-button
          :labels="labels"
          :fields="fields"
          :loading="isLoading"
          :data="mailboxProvidersList"
          fileName="mailbox_providers"
        />
      </v-col>
    </page-header>
    <v-row class="d-flex justify-center pt-3" no-gutters>
      <v-col cols="12" class="pt-3">
        <template>
          <mailbox-providers-table
            :isLoading="isLoading"
            :data="mailboxProvidersList"
            default-sort-order="name"
            :update-records="updateRecords"
            @get-records="getMailboxProvidersList"
            @update-mailbox-provider="updateMailboxProvider"
          />
        </template>
      </v-col>
    </v-row>
  </section>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { MailboxProvidersTable } from "@/components/tables";
import { TitleMixin, SharedUtilsMixin } from "@/mixins";

import { PageHeader, DebounceSearchTextField } from "@/components/shared";
import CsvButton from "@/components/widgets/CsvButton.vue";
import { csvFileLabels, hasProp, isEmpty } from "@/utils/common.utils";
import headers from "@/data/table-headers/mailbox-providers.headers.json";

import { ACCESS_TYPES } from "@/constants";

/**
 * Manage mailbox providers view
 */
export default {
  name: "ManageMailboxProvider",
  /**
  |--------------------------------------------------
  | Title of the page
  |--------------------------------------------------
  */
  title: "Mailbox providers",
  /**
  |--------------------------------------------------
  | Mixins
  |--------------------------------------------------
  */
  mixins: [TitleMixin, SharedUtilsMixin],
  /**
  |--------------------------------------------------
  | Components
  |--------------------------------------------------
  */
  components: {
    CsvButton,
    PageHeader,
    MailboxProvidersTable,
    DebounceSearchTextField,
  },
  /**
  |--------------------------------------------------
  | Data properties
  |--------------------------------------------------
  */
  data() {
    return {
      isLoading: false,
      updateRecords: false,
      searchMailboxName: "",
      mailboxParams: {},
      labels: csvFileLabels(headers),
      mailboxProvidersList: [],
    };
  },
  /**
  |--------------------------------------------------
  | Computed properties
  |--------------------------------------------------
  */
  computed: {
    ...mapGetters({
      mailboxProviders: "account/mailboxProviders",
    }),
    /**
     * Seeds listing sorting configuration
     */
    sortConfig() {
      return this.getSortConfig("mailboxParams", "name");
    },
    /**
     * Fields that needs to be downloaded in Csv file
     */
    fields() {
      return Object.keys(this.labels);
    },
    /**
     * Manage users breadcrumbs
     * @type {Array}
     */
    breadcrumbs() {
      return [
        {
          title: this.$dictionary.app.mailboxProvider.index.title,
          type: this.$appConfig.breadcrumbs.types.route,
        },
      ];
    },
  },
  /**
  |--------------------------------------------------
  | Methods
  |--------------------------------------------------
  */
  methods: {
    /**
     * Maps store actions in the component
     */
    ...mapActions({
      getMailboxProviders: "account/getMailboxProviders",
    }),
    /**
     * Shows edit mailbox provider drawer
     * @listens update-mailbox-provider To show edit mailbox provider drawer
     */
    updateMailboxProvider(item) {
      this.selectedMailboxProvider = item;
    },
    /**
     * Fetches list of mailbox providers
     * @param {Object} params Request query params to fetch mailbox providers
     */
    async getMailboxProvidersList(params) {
      try {
        this.mailboxParams = params;

        if (
          this.searchMailboxName &&
          !hasProp(this.mailboxParams, "q[name_cont]")
        ) {
          this.mailboxParams["q[name_cont]"] = this.searchMailboxName;
        }
        this.isLoading = true;
        await this.getMailboxProviders(params);
        this.mailboxProvidersList = this.computeMailboxProviders(
          this.mailboxProviders
        );
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: this.$errorMessage(error),
          type: this.snackbarTypes.error,
        });
      } finally {
        this.isLoading = false;
      }
    },
    /**
     * Computes mailbox provider imap host value in mailbox providers list
     * @returns {Array} List of compurted mailbox providers list
     */
    computeMailboxProviders(providers) {
      if (isEmpty(providers)) return [];

      const getAccessType = (val) =>
        ACCESS_TYPES.find(({ value }) => value === val);

      return providers.map(({ access_type, ...rest }) => ({
        ...rest,
        access_type: getAccessType(access_type)?.name,
      }));
    },
  },
};
</script>
